import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { t } from 'utils/i18n';
import type { FrameStatus } from '../steps/steps-api';
import {
    chunk, detectionOf, Detection, FIND_CHUNK, frameStatuses, StateLike,
} from './reference-model';
import { findModels, ReferencePhoto } from './references-api';

export interface ReferencesContextValue {
    jobId: number;
    taskId: number;
    currentFrame: number;
    detections: Detection[]; // the whole job, by frame then clientID
    photoDetections: Detection[]; // current frame only
    frameStatus: Record<number, FrameStatus>;
    activeClientID: number | null;
    finding: { done: number; total: number } | null;
    error: string | null;
    picker: Detection | null; // the detection whose drawer is open
    backendAPI: string;
    photoNumber(frame: number): number; // 1-based number in the references strip
    find(targets: Detection[]): Promise<void>;
    cancelFind(): void; // the chunk in flight finishes, the rest is not sent
    setModel(target: Detection, value: string): Promise<void>;
    remove(targets: Detection[]): Promise<void>; // deletes the shapes from their photos and saves
    setFlags(targets: Detection[], flags: { hidden?: boolean; lock?: boolean }): Promise<void>;
    photoCount: number; // photos of this job
    select(target: Detection): void;
    openPicker(target: Detection | null): void;
}

export const ReferencesContext = createContext<ReferencesContextValue | null>(null);

export function useReferencesContext(): ReferencesContextValue | null {
    return useContext(ReferencesContext);
}

export interface ReferencesProviderProps {
    jobId: number; taskId: number; currentFrame: number; activeClientID: number | null;
    version: unknown; // changes whenever annotations change
    photos: ReferencePhoto[];
    backendAPI: string;
    collect(): Promise<StateLike[]>; // every annotation object of the job
    hasUnsavedChanges(): boolean;
    save(): Promise<void>; // throws when changes stay unsaved
    reload(): Promise<void>; // re-reads annotations from the server
    update(states: StateLike[]): Promise<void>; // updateAnnotationsAsync
    remove(states: StateLike[]): Promise<void>; // removeObjectAsync each, then save
    activate(frame: number, clientID: number): void;
    children: React.ReactNode;
}

type PickerKey = { serverID: number | null; clientID: number } | null;

function message(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

export function ReferencesProvider(props: ReferencesProviderProps): JSX.Element {
    const {
        jobId, taskId, currentFrame, activeClientID, version, photos, backendAPI,
        collect, hasUnsavedChanges, save, reload, update, remove: removeStates, activate, children,
    } = props;
    const [states, setStates] = useState<StateLike[]>([]);
    const [finding, setFinding] = useState<{ done: number; total: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pickerKey, setPickerKey] = useState<PickerKey>(null);
    const mounted = useRef(true);
    const cancelled = useRef(false);
    useEffect(() => () => { mounted.current = false; }, []);

    const refresh = useCallback(async (): Promise<StateLike[]> => {
        const next = await collect();
        if (mounted.current) setStates(next);
        return next;
    }, [collect]);

    useEffect(() => { refresh().catch(() => undefined); }, [refresh, version]);

    const detections = useMemo(() => states
        .map(detectionOf)
        .filter((item): item is Detection => item !== null)
        .sort((a, b) => a.frame - b.frame || a.clientID - b.clientID), [states]);
    const photoDetections = useMemo(
        () => detections.filter((item) => item.frame === currentFrame), [detections, currentFrame],
    );
    const frameStatus = useMemo<Record<number, FrameStatus>>(() => frameStatuses(detections), [detections]);

    // reload() renews clientIDs, so the open drawer follows the server id when there is one
    const picker = useMemo(() => {
        if (!pickerKey) return null;
        if (pickerKey.serverID !== null) {
            return detections.find((item) => item.serverID === pickerKey.serverID) ?? null;
        }
        return detections.find((item) => item.clientID === pickerKey.clientID) ?? null;
    }, [pickerKey, detections]);

    const find = useCallback(async (targets: Detection[]): Promise<void> => {
        setError(null);
        cancelled.current = false;
        let sent = false;
        try {
            if (hasUnsavedChanges()) await save();
            const fresh = (await refresh()).map(detectionOf).filter((item): item is Detection => item !== null);
            const wanted = new Set(targets.map((item) => item.clientID));
            // shapes drawn since the last save get their server ids only now
            const ids = fresh.filter((item) => wanted.has(item.clientID) && item.serverID !== null)
                .map((item) => item.serverID as number);
            let done = 0;
            if (mounted.current) setFinding({ done, total: ids.length });
            for (const part of chunk(ids, FIND_CHUNK)) {
                // after leaving the page the store holds another job, so stop sending and never reload it
                if (!mounted.current || cancelled.current) break;
                sent = true;
                await findModels(jobId, taskId, part);
                done += part.length;
                if (mounted.current) setFinding({ done, total: ids.length });
            }
        } catch (caught) {
            if (mounted.current) setError(t('Could not find models: {error}', { error: message(caught) }));
        } finally {
            try {
                if (sent && mounted.current) {
                    await reload();
                    await refresh().catch(() => undefined);
                }
            } finally {
                if (mounted.current) setFinding(null);
            }
        }
    }, [hasUnsavedChanges, save, refresh, reload, jobId, taskId]);

    const cancelFind = useCallback(() => { cancelled.current = true; }, []);

    const setModel = useCallback(async (target: Detection, value: string): Promise<void> => {
        const state = states.find((item) => item.clientID === target.clientID);
        if (!state) return;
        state.attributes = { [target.specId]: value };
        await update([state]);
    }, [states, update]);

    const remove = useCallback(async (targets: Detection[]): Promise<void> => {
        const wanted = new Set(targets.map((item) => item.clientID));
        setError(null);
        try {
            await removeStates(states.filter((item) => wanted.has(item.clientID)));
        } catch (caught) {
            setError(t('Could not delete: {error}', { error: message(caught) }));
        }
    }, [states, removeStates]);

    const setFlags = useCallback(async (targets: Detection[], flags: { hidden?: boolean; lock?: boolean }) => {
        const wanted = new Set(targets.map((item) => item.clientID));
        const chosen = states.filter((item) => wanted.has(item.clientID));
        chosen.forEach((item) => Object.assign(item, flags));
        await update(chosen);
    }, [states, update]);
    const photoCount = useMemo(() => photos.filter((item) => item.jobId === jobId).length, [photos, jobId]);

    const select = useCallback((target: Detection) => activate(target.frame, target.clientID), [activate]);
    const openPicker = useCallback((target: Detection | null) => {
        setPickerKey(target ? { serverID: target.serverID, clientID: target.clientID } : null);
    }, []);
    const photoNumber = useCallback((frame: number) => {
        const photo = photos.find((item) => item.jobId === jobId && item.frame === frame);
        return photo ? photo.index + 1 : frame + 1;
    }, [photos, jobId]);

    const value = useMemo<ReferencesContextValue>(() => ({
        jobId,
        taskId,
        currentFrame,
        detections,
        photoDetections,
        frameStatus,
        activeClientID,
        finding,
        error,
        picker,
        backendAPI,
        photoNumber,
        find,
        cancelFind,
        setModel,
        remove,
        setFlags,
        photoCount,
        select,
        openPicker,
    }), [jobId, taskId, currentFrame, detections, photoDetections, frameStatus, activeClientID, finding, error,
        picker, backendAPI, photoNumber, find, cancelFind, setModel, remove, setFlags, photoCount, select, openPicker]);

    return <ReferencesContext.Provider value={value}>{children}</ReferencesContext.Provider>;
}
